import React, { Fragment, useCallback, useEffect, useState, useMemo } from 'react';
import { Flex, Box, Button, NakedButton, Text, Wrapper } from 'roo-ui/components';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import CashSlider from './CashSlider';
import Currency from 'components/Currency';
import { getPayableNowCashAmount, getTotalPayableAfterCredits } from 'store/checkout/checkoutSelectors';
import { updatePayments } from 'store/checkout/checkoutActions';
import { useDataLayer } from 'hooks/useDataLayer';
import Modal from 'components/Modal';
import { useModal } from 'lib/hooks';
import {
  PaymentMethodSummaryContainer,
  PaymentMethodSummaryButton,
} from 'pages/Checkout/CustomerDetails/Steps/PaymentOptions/PaymentMethodSummary';

const ModalFooter = ({ applyCashFromModal, closeCashModal }) => {
  return (
    <Fragment>
      <Box borderTop={1} borderColor="greys.porcelain" mt={'auto'}>
        <Flex px={6} py={4} justifyContent="space-between">
          <NakedButton onClick={closeCashModal} color="brand.primary" data-testid="close-modal-button">
            <Text fontWeight="bold" letterSpacing="1px">
              CANCEL
            </Text>
          </NakedButton>
          <Button onClick={applyCashFromModal} data-testid="apply-cash-button" variant="primary">
            ADD CASH
          </Button>
        </Flex>
      </Box>
    </Fragment>
  );
};

ModalFooter.propTypes = {
  applyCashFromModal: PropTypes.func.isRequired,
  closeCashModal: PropTypes.func.isRequired,
};

const Cash = ({ ...rest }) => {
  const { openModal, closeModal, modalProps } = useModal();
  const dispatch = useDispatch();
  const payableNowCashAmount = useSelector(getPayableNowCashAmount);
  const totalPayableAfterCredits = useSelector(getTotalPayableAfterCredits);
  const hasCashAmountGreaterThanZero = payableNowCashAmount.greaterThan(0);
  const [bufferedCash, setBufferedCash] = useState();
  const { emitInteractionEvent } = useDataLayer();

  const defaultCash = useMemo(() => {
    return { amount: payableNowCashAmount };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payableNowCashAmount.toNumber()]);

  useEffect(() => {
    setBufferedCash(defaultCash);
  }, [defaultCash]);

  const updateCash = useCallback(
    (cash) => {
      dispatch(updatePayments({ cash: { payableNow: cash } }));
      setBufferedCash(cash);
    },
    [dispatch],
  );

  const applyCashFromModal = useCallback(() => {
    updateCash({ amount: bufferedCash.amount });

    emitInteractionEvent({
      type: 'Use Cash for Deposit Pop Up',
      value: 'Use Cash Apply Button Selected',
    });

    closeModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bufferedCash, updateCash, closeModal]);

  const editCash = useCallback(() => {
    openModal();

    emitInteractionEvent({
      type: 'Use Cash for Deposit Pop Up',
      value: 'Use Cash Opened',
    });
  }, [emitInteractionEvent, openModal]);

  const closeCashModal = useCallback(() => {
    closeModal();
    setBufferedCash(defaultCash);

    emitInteractionEvent({
      type: 'Use Cash for Deposit Pop Up',
      value: 'Use Cash Closed',
    });
  }, [closeModal, defaultCash, emitInteractionEvent]);

  const isCashEditable = totalPayableAfterCredits.greaterThan(0);

  return (
    <Wrapper {...rest}>
      <PaymentMethodSummaryContainer borderRadius="exagerated" border={1}>
        <Flex alignItems={['flex-start', 'center']} justifyContent="space-between" width="100%" flexDirection={['column', 'row']}>
          <Text fontWeight="bold" fontSize={['sm', 'base']}>
            Cash
          </Text>
          {hasCashAmountGreaterThanZero && (
            <Currency
              amount={payableNowCashAmount}
              currency="AUD"
              fontSize={['xs', 'base']}
              mr={[0, 4]}
              data-testid="payable-now-cash-amount"
            />
          )}
        </Flex>
        <PaymentMethodSummaryButton onClick={editCash} disabled={!isCashEditable} variant="primary" data-testid="edit-cash-button">
          {hasCashAmountGreaterThanZero ? 'CHANGE' : 'ADD'}
        </PaymentMethodSummaryButton>
      </PaymentMethodSummaryContainer>
      {modalProps.isOpen && (
        <Modal
          {...modalProps}
          onRequestClose={closeCashModal}
          title="Add a cash deposit"
          padding={0}
          footerComponent={() => <ModalFooter closeCashModal={closeCashModal} applyCashFromModal={applyCashFromModal} />}
        >
          <Box padding={[6, 8]}>
            <CashSlider cash={bufferedCash} updateCash={setBufferedCash} />
          </Box>
        </Modal>
      )}
    </Wrapper>
  );
};

export default Cash;
